import Vue from "vue";
import Vuex from "vuex";
import {
  retrieveResource,
  updateResource,
  deleteResource,
  createResource,
  convertArrayToKeyedById
} from "@/services/server.js";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    currentUser: undefined,
    credentials: undefined,
    gapiCredentials: undefined,
    users: {},
    departments: {},
    links: {},
    categories: {},
    loading: false,
    alert: {
      status: undefined,
      message: undefined
    }
  },
  mutations: {
    updateAlert(state, payload) {
      let newAlert = {
        status: payload.status,
        message: payload.message
      };
      state.alert = newAlert;
    },
    refreshAlert(state) {
      state.alert = { status: undefined, message: undefined };
    },
    showLoading(state) {
      state.loading = true;
    },
    hideLoading(state) {
      state.loading = false;
    },
    addCurrentUser(state, payload) {
      payload = { ...payload, id: payload.email.split("@gotugende.com")[0] };
      state.currentUser = payload;
    },
    addCredentials(state, payload) {
      state.credentials = payload;
    },
    addGapiCredentials(state, payload) {
      state.gapiCredentials = payload;
    },
    removeCurrentUser(state) {
      state.currentUser = undefined;
    },
    removeCredentials(state) {
      state.credentials = undefined;
    },
    refreshUsers(state, payload) {
      state.users = payload;
    },
    refreshDepartments(state, payload) {
      state.departments = payload;
    },
    refreshCategories(state, payload) {
      state.categories = payload;
    },
    refreshLinks(state, payload) {
      state.links = payload;
    },
    addUser(state, payload) {
      state.users = {
        ...state.users,
        [payload.id]: payload
      };
    },
    addDepartment(state, payload) {
      state.departments = {
        ...state.departments,
        [payload.id]: payload
      };
    },
    addCategory(state, payload) {
      state.categories = {
        ...state.categories,
        [payload.id]: payload
      };
    },
    addLink(state, payload) {
      state.links = {
        ...state.links,
        [payload.id]: payload
      };
    },
    updateUser(state, payload) {
      state.users[payload.id] = payload;
    },
    updateLink(state, payload) {
      state.links[payload.id] = payload;
    },
    updateDepartment(state, payload) {
      state.departments[payload.id] = payload;
    },
    updateCategory(state, payload) {
      state.categories[payload.id] = payload;
    },
    destroyUser(state, payload) {
      let tmp = { ...state.users };
      delete tmp[payload.id];
      state.users = tmp;
    },
    destroyLink(state, payload) {
      let tmp = { ...state.links };
      delete tmp[payload.id];
      state.links = tmp;
    },
    destroyDepartment(state, payload) {
      let tmp = { ...state.departments };
      delete tmp[payload.id];
      state.departments = tmp;
    },
    destroyCategory(state, payload) {
      let tmp = { ...state.categories };
      delete tmp[payload.id];
      state.categories = tmp;
    }
  },
  actions: {
    async retrieveUsersFromServer({ commit }) {
      return retrieveResource("users", (err, list) => {
        let response = {
          status: "success",
          message: "users retrieved successfully"
        };
        if (!err) {
          commit("refreshUsers", convertArrayToKeyedById(list));
        } else {
          response = {
            status: "error",
            message: `Error retrieving users: ${err.message}`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async retrieveLinksFromServer({ commit }) {
      return retrieveResource("links", (err, list) => {
        let response = {
          status: "success",
          message: "links retrieved successfully"
        };
        if (!err) {
          commit("refreshLinks", convertArrayToKeyedById(list));
        } else {
          response = {
            status: "error",
            message: `Error retrieving links: ${err.message}`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async retrieveCategoriesFromServer({ commit }) {
      return retrieveResource("categories", (err, list) => {
        let response = {
          status: "success",
          message: "categories retrieved successfully"
        };
        if (!err) {
          commit("refreshCategories", convertArrayToKeyedById(list));
        } else {
          response = { status: "error", message: err.message };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async retrieveDepartmentsFromServer({ commit }) {
      return retrieveResource("departments", (err, list) => {
        let response = {
          status: "success",
          message: "departments retrieved successfully"
        };
        if (!err) {
          commit("refreshDepartments", convertArrayToKeyedById(list));
        } else {
          response = {
            status: "error",
            message: `Error retrieving departments: ${err.message}`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async createUser({ commit }, payload) {
      payload = { ...payload, id: payload.email.split("@gotugende.com")[0] };
      return createResource("users", payload, (err, result) => {
        let response = {
          status: "success",
          message: `"${payload.displayName}" has been created successfully`
        };
        if (!err) {
          commit("addUser", result);
        } else {
          response = {
            status: "error",
            message: `Error creating user '${payload.displayName}': ${
              err.message
            }`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async createLink({ commit, state }, payload) {
      const authorDetails = {
        displayName: state.currentUser.displayName,
        email: state.currentUser.email
      };
      const categoryDetails = {
        name: payload.category.name,
        slug: payload.category.slug
      };
      const departmentDetails = {
        name: payload.department.name,
        slug: payload.department.slug
      };
      payload = {
        ...payload,
        author: authorDetails,
        category: categoryDetails,
        department: departmentDetails
      };
      return createResource("links", payload, (err, result) => {
        let response = {
          status: "success",
          message: `"${payload.label}"" link has been created successfully`
        };
        if (!err) {
          commit("addLink", result);
        } else {
          response = {
            status: "error",
            message: `Error creating link '${payload.label}': ${err.message}`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async createDepartment({ commit }, payload) {
      return createResource("departments", payload, (err, result) => {
        let response = {
          status: "success",
          message: `"${payload.name}"" department has been created successfully`
        };
        if (!err) {
          commit("addDepartment", result);
        } else {
          response = {
            status: "error",
            message: `Error creating department '${payload.name}': ${
              err.message
            }`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async createCategory({ commit }, payload) {
      return createResource("categories", payload, (err, result) => {
        let response = {
          status: "success",
          message: `"${payload.name}" category has been created successfully`
        };
        if (!err) {
          commit("addCategory", result);
        } else {
          response = {
            status: "error",
            message: `Error creating category '${payload.name}': ${err.message}`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async updateUser({ commit }, payload) {
      payload = { ...payload, id: payload.email.split("@gotugende.com")[0] };
      return updateResource("users", payload.id, payload, (err, result) => {
        let response = {
          status: "success",
          message: `"${payload.displayName}" has been updated successfully`
        };
        if (!err) {
          // if user is admin, add email to admin list or else delete it
          if (payload.isAdmin) {
            createResource("admins", { id: payload.id }, () => {});
          } else {
            deleteResource("admins", payload.id, () => {});
          }
          commit("updateUser", result);
        } else {
          response = {
            status: "error",
            message: `Error updating user '${payload.displayName}': ${
              err.message
            }`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async updateLink({ commit }, payload) {
      return updateResource("links", payload.id, payload, (err, result) => {
        let response = {
          status: "success",
          message: `"${payload.label}" link been updated successfully`
        };
        if (!err) {
          commit("updateLink", result);
        } else {
          response = {
            status: "error",
            message: `Error updating link '${payload.label}': ${err.message}`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async updateDepartment({ commit }, payload) {
      return updateResource(
        "departments",
        payload.id,
        payload,
        (err, result) => {
          let response = {
            status: "success",
            message: `"${payload.name}" department been updated successfully`
          };
          if (!err) {
            commit("updateDepartment", result);
          } else {
            response = {
              status: "error",
              message: `Error updating department '${payload.name}': ${
                err.message
              }`
            };
          }
          commit("updateAlert", response);
          return response;
        }
      );
    },
    async updateCategory({ commit }, payload) {
      return updateResource(
        "categories",
        payload.id,
        payload,
        (err, result) => {
          let response = {
            status: "success",
            message: `"${payload.name}" category been updated successfully`
          };
          if (!err) {
            commit("updateCategory", result);
          } else {
            response = {
              status: "error",
              message: `Error updating category '${payload.name}': ${
                err.message
              }`
            };
          }
          commit("updateAlert", response);
          return response;
        }
      );
    },
    async destroyUser({ commit }, payload) {
      return deleteResource("users", payload.id, (err, result) => {
        let response = {
          status: "success",
          message: `"${payload.displayName}" deleted successfully`
        };
        if (!err) {
          // if was admin, delete him/her
          deleteResource("admins", payload.id, () => {});
          commit("destroyUser", { id: result });
        } else {
          response = {
            status: "error",
            message: `Error deleting user: ${err.message}`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async destroyLink({ commit }, payload) {
      return deleteResource("links", payload.id, (err, result) => {
        let response = {
          status: "success",
          message: `"${payload.label}" link deleted successfully`
        };
        if (!err) {
          commit("destroyLink", { id: result });
        } else {
          response = {
            status: "error",
            message: `Error deleting link: ${err.message}`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async destroyDepartment({ commit }, payload) {
      return deleteResource("departments", payload.id, (err, result) => {
        let response = {
          status: "success",
          message: `"${payload.name}" department deleted successfully`
        };
        if (!err) {
          commit("destroyDepartment", { id: result });
        } else {
          response = {
            status: "error",
            message: `Error deleting department: ${err.message}`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    },
    async destroyCategory({ commit }, payload) {
      return deleteResource("categories", payload.id, (err, result) => {
        let response = {
          status: "success",
          message: `"${payload.name}" category deleted successfully`
        };
        if (!err) {
          commit("destroyCategory", { id: result });
        } else {
          response = {
            status: "error",
            message: `Error deleting category: ${err.message}`
          };
        }
        commit("updateAlert", response);
        return response;
      });
    }
  }
});
